import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { osceAPI } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  Play,
  Pause,
  Stop,
  Clock,
  CheckCircle,
  XCircle,
  Warning,
  User,
  Stethoscope,
  ChatCircle,
  Flask,
  Eye,
  CaretRight,
  CaretLeft,
  Flag,
  Spinner,
  ArrowRight,
  Trophy,
  Target,
  ListChecks
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Station type configurations
const STATION_ICONS = {
  anamnesis: ChatCircle,
  examen_fisico: Stethoscope,
  procedimiento: Flask,
  comunicacion: ChatCircle,
  interpretacion: Eye,
};

const STATION_COLORS = {
  anamnesis: 'bg-blue-500',
  examen_fisico: 'bg-green-500',
  procedimiento: 'bg-purple-500',
  comunicacion: 'bg-yellow-500',
  interpretacion: 'bg-orange-500',
};

export default function OSCELivePage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Session state
  const [session, setSession] = useState(null);
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Current station state
  const [currentStationIndex, setCurrentStationIndex] = useState(0);
  const [stationResults, setStationResults] = useState({});
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const timerRef = useRef(null);
  
  // Confirmation dialogs
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [finalResults, setFinalResults] = useState(null);

  // Load session and exam data
  useEffect(() => {
    loadSessionData();
  }, [sessionId]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      const sessionRes = await osceAPI.getSessionById(sessionId);
      const sessionData = sessionRes.data;
      setSession(sessionData);
      
      if (sessionData.exam_id) {
        const examRes = await osceAPI.getExamById(sessionData.exam_id);
        setExam(examRes.data);
        
        // Initialize station results
        const initialResults = {};
        examRes.data.estaciones?.forEach(station => {
          initialResults[station.id] = {
            criterios: {},
            observaciones: '',
            completado: false
          };
          station.criterios?.forEach(criterio => {
            initialResults[station.id].criterios[criterio.id] = {
              cumplido: false,
              puntos: 0
            };
          });
        });
        setStationResults(initialResults);
        
        // Set initial timer for first station
        if (examRes.data.estaciones?.length > 0) {
          setTimeRemaining(examRes.data.estaciones[0].duracion_minutos * 60);
        }
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Error al cargar la sesión');
    } finally {
      setLoading(false);
    }
  };

  // Timer logic
  useEffect(() => {
    if (isRunning && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up for this station
            toast.warning('¡Tiempo agotado para esta estación!');
            handleAutoAdvance();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRunning, timeRemaining]);

  const handleAutoAdvance = () => {
    if (currentStationIndex < (exam?.estaciones?.length || 0) - 1) {
      // Auto-mark current station as completed
      markStationCompleted();
      handleNextStation();
    } else {
      // Last station - end exam
      handleEndExam();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const currentStation = exam?.estaciones?.[currentStationIndex];
  const StationIcon = currentStation ? STATION_ICONS[currentStation.tipo] || Target : Target;

  // Handle criterion toggle
  const handleCriterionToggle = (criterioId, criterio) => {
    if (!currentStation) return;
    
    setStationResults(prev => ({
      ...prev,
      [currentStation.id]: {
        ...prev[currentStation.id],
        criterios: {
          ...prev[currentStation.id].criterios,
          [criterioId]: {
            cumplido: !prev[currentStation.id].criterios[criterioId]?.cumplido,
            puntos: !prev[currentStation.id].criterios[criterioId]?.cumplido ? criterio.puntos_maximos : 0
          }
        }
      }
    }));
  };

  // Handle observations change
  const handleObservationsChange = (value) => {
    if (!currentStation) return;
    
    setStationResults(prev => ({
      ...prev,
      [currentStation.id]: {
        ...prev[currentStation.id],
        observaciones: value
      }
    }));
  };

  // Mark station as completed
  const markStationCompleted = () => {
    if (!currentStation) return;
    
    setStationResults(prev => ({
      ...prev,
      [currentStation.id]: {
        ...prev[currentStation.id],
        completado: true
      }
    }));
  };

  // Navigate to next station
  const handleNextStation = () => {
    if (currentStationIndex < (exam?.estaciones?.length || 0) - 1) {
      markStationCompleted();
      const nextIndex = currentStationIndex + 1;
      setCurrentStationIndex(nextIndex);
      const nextStation = exam.estaciones[nextIndex];
      setTimeRemaining(nextStation.duracion_minutos * 60);
    }
  };

  // Navigate to previous station
  const handlePrevStation = () => {
    if (currentStationIndex > 0) {
      setCurrentStationIndex(currentStationIndex - 1);
      const prevStation = exam.estaciones[currentStationIndex - 1];
      setTimeRemaining(prevStation.duracion_minutos * 60);
    }
  };

  // Start/pause timer
  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };

  // Calculate station score
  const calculateStationScore = (stationId) => {
    const result = stationResults[stationId];
    if (!result) return { obtained: 0, max: 0 };
    
    const station = exam?.estaciones?.find(s => s.id === stationId);
    if (!station) return { obtained: 0, max: 0 };
    
    let obtained = 0;
    let max = 0;
    
    station.criterios?.forEach(criterio => {
      max += criterio.puntos_maximos;
      if (result.criterios[criterio.id]?.cumplido) {
        obtained += criterio.puntos_maximos;
      }
    });
    
    return { obtained, max };
  };

  // Calculate total score
  const calculateTotalScore = () => {
    let totalObtained = 0;
    let totalMax = 0;
    
    exam?.estaciones?.forEach(station => {
      const score = calculateStationScore(station.id);
      totalObtained += score.obtained;
      totalMax += score.max;
    });
    
    return { obtained: totalObtained, max: totalMax, percentage: totalMax > 0 ? Math.round((totalObtained / totalMax) * 100) : 0 };
  };

  // Check for critical criteria failures
  const checkCriticalFailures = () => {
    const failures = [];
    
    exam?.estaciones?.forEach(station => {
      station.criterios?.forEach(criterio => {
        if (criterio.es_critico && !stationResults[station.id]?.criterios[criterio.id]?.cumplido) {
          failures.push({
            station: station.nombre,
            criterio: criterio.descripcion
          });
        }
      });
    });
    
    return failures;
  };

  // End exam and calculate results
  const handleEndExam = async () => {
    setSaving(true);
    try {
      // Mark current station as completed
      markStationCompleted();
      
      // Calculate final results
      const totalScore = calculateTotalScore();
      const criticalFailures = checkCriticalFailures();
      
      const results = {
        score: totalScore,
        criticalFailures,
        passed: criticalFailures.length === 0 && totalScore.percentage >= 60,
        stationResults: { ...stationResults }
      };
      
      setFinalResults(results);
      
      // Submit results to server
      for (const station of exam.estaciones) {
        const stationScore = calculateStationScore(station.id);
        await osceAPI.submitStationResult(sessionId, station.id, {
          criterios_evaluados: stationResults[station.id]?.criterios || {},
          observaciones: stationResults[station.id]?.observaciones || '',
          puntos_obtenidos: stationScore.obtained,
          puntos_maximos: stationScore.max
        });
      }
      
      // End session
      await osceAPI.endSession(sessionId);
      
      setShowEndDialog(false);
      setShowResultsDialog(true);
      setIsRunning(false);
      
      toast.success('Evaluación finalizada');
    } catch (error) {
      console.error('Error ending exam:', error);
      toast.error('Error al finalizar la evaluación');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  if (!exam) {
    return (
      <>
        <div className="text-center py-16">
          <Warning size={48} className="mx-auto text-yellow-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Sesión no encontrada</h2>
          <p className="text-slate-500 mb-4">No se pudo cargar la información de la evaluación.</p>
          <Button onClick={() => navigate('/app/evaluations')}>Volver a Evaluaciones</Button>
        </div>
      </>
    );
  }

  const currentScore = calculateStationScore(currentStation?.id);
  const totalScore = calculateTotalScore();
  const progress = ((currentStationIndex + 1) / exam.estaciones.length) * 100;

  return (
    <>
      <div className="space-y-4" data-testid="osce-live-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-4 rounded-lg shadow-sm border">
          <div>
            <h1 className="text-xl font-bold text-slate-900">{exam.nombre}</h1>
            <p className="text-sm text-slate-500">
              Estación {currentStationIndex + 1} de {exam.estaciones.length}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Timer */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              timeRemaining < 60 ? 'bg-red-100 text-red-700' : 
              timeRemaining < 120 ? 'bg-yellow-100 text-yellow-700' : 
              'bg-blue-100 text-blue-700'
            }`}>
              <Clock size={20} weight="bold" />
              <span className="text-2xl font-mono font-bold">{formatTime(timeRemaining)}</span>
            </div>
            
            {/* Timer Controls */}
            <div className="flex gap-2">
              <Button 
                variant={isRunning ? "outline" : "default"}
                size="icon"
                onClick={toggleTimer}
                data-testid="timer-toggle-btn"
              >
                {isRunning ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
              </Button>
              <Button 
                variant="destructive" 
                size="icon"
                onClick={() => setShowEndDialog(true)}
                data-testid="end-exam-btn"
              >
                <Stop size={18} weight="fill" />
              </Button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white p-3 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-slate-500">Progreso del examen</span>
            <span className="font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="flex mt-2 gap-1">
            {exam.estaciones.map((station, idx) => (
              <div 
                key={station.id}
                className={`flex-1 h-2 rounded-full cursor-pointer transition-colors ${
                  idx < currentStationIndex ? 'bg-green-500' :
                  idx === currentStationIndex ? 'bg-blue-500' :
                  'bg-slate-200'
                }`}
                onClick={() => {
                  setCurrentStationIndex(idx);
                  setTimeRemaining(station.duracion_minutos * 60);
                }}
                title={station.nombre}
              />
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Station Info Panel */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${STATION_COLORS[currentStation?.tipo] || 'bg-slate-500'} text-white`}>
                    <StationIcon size={24} weight="bold" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{currentStation?.nombre}</CardTitle>
                    <Badge variant="outline" className="mt-1">
                      {currentStation?.duracion_minutos} minutos
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Instrucciones</h4>
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                      {currentStation?.instrucciones_estudiante || 'No hay instrucciones específicas para esta estación.'}
                    </p>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Puntuación de la estación</h4>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-blue-600">
                        {currentScore.obtained} / {currentScore.max}
                      </span>
                      <Badge variant={currentScore.obtained === currentScore.max ? 'default' : 'secondary'}>
                        {currentScore.max > 0 ? Math.round((currentScore.obtained / currentScore.max) * 100) : 0}%
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total Score Card */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardContent className="pt-4">
                <div className="text-center">
                  <Trophy size={32} className="mx-auto text-blue-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Puntuación Total</p>
                  <p className="text-3xl font-bold text-blue-700">
                    {totalScore.obtained} / {totalScore.max}
                  </p>
                  <Badge className="mt-2" variant={totalScore.percentage >= 60 ? 'default' : 'destructive'}>
                    {totalScore.percentage}%
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1 gap-2"
                onClick={handlePrevStation}
                disabled={currentStationIndex === 0}
                data-testid="prev-station-btn"
              >
                <CaretLeft size={16} />
                Anterior
              </Button>
              <Button 
                className="flex-1 gap-2"
                onClick={handleNextStation}
                disabled={currentStationIndex === exam.estaciones.length - 1}
                data-testid="next-station-btn"
              >
                Siguiente
                <CaretRight size={16} />
              </Button>
            </div>
          </div>

          {/* Checklist Panel */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ListChecks size={20} />
                    Criterios de Evaluación
                  </CardTitle>
                  <Badge variant="outline">
                    {Object.values(stationResults[currentStation?.id]?.criterios || {}).filter(c => c.cumplido).length} / 
                    {currentStation?.criterios?.length || 0} completados
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {currentStation?.criterios?.map((criterio, idx) => {
                      const isChecked = stationResults[currentStation.id]?.criterios[criterio.id]?.cumplido;
                      
                      return (
                        <div 
                          key={criterio.id}
                          className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                            isChecked 
                              ? 'border-green-500 bg-green-50' 
                              : criterio.es_critico 
                                ? 'border-red-200 bg-red-50 hover:border-red-300' 
                                : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                          onClick={() => handleCriterionToggle(criterio.id, criterio)}
                          data-testid={`criterion-${criterio.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox 
                              checked={isChecked}
                              onCheckedChange={() => handleCriterionToggle(criterio.id, criterio)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className={`font-medium ${isChecked ? 'text-green-700' : 'text-slate-700'}`}>
                                  {idx + 1}. {criterio.descripcion}
                                </span>
                                <div className="flex items-center gap-2">
                                  {criterio.es_critico && (
                                    <Badge variant="destructive" className="text-xs">
                                      <Flag size={12} className="mr-1" />
                                      Crítico
                                    </Badge>
                                  )}
                                  <Badge variant={isChecked ? 'default' : 'outline'}>
                                    {isChecked ? criterio.puntos_maximos : 0} / {criterio.puntos_maximos} pts
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {(!currentStation?.criterios || currentStation.criterios.length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        <Target size={32} className="mx-auto mb-2 opacity-50" />
                        <p>No hay criterios definidos para esta estación</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
                
                <Separator className="my-4" />
                
                {/* Observations */}
                <div className="space-y-2">
                  <Label htmlFor="observations">Observaciones del evaluador</Label>
                  <Textarea
                    id="observations"
                    placeholder="Anote observaciones adicionales sobre el desempeño del estudiante..."
                    value={stationResults[currentStation?.id]?.observaciones || ''}
                    onChange={(e) => handleObservationsChange(e.target.value)}
                    rows={3}
                    data-testid="observations-textarea"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* End Exam Dialog */}
        <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Warning size={24} />
                Finalizar Evaluación
              </DialogTitle>
              <DialogDescription>
                ¿Está seguro de que desea finalizar la evaluación? Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600 mb-2">Resumen actual:</p>
                <ul className="text-sm space-y-1">
                  <li>• Estaciones completadas: {Object.values(stationResults).filter(r => r.completado).length} / {exam.estaciones.length}</li>
                  <li>• Puntuación actual: {totalScore.obtained} / {totalScore.max} ({totalScore.percentage}%)</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEndDialog(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleEndExam} disabled={saving}>
                {saving ? <Spinner size={18} className="animate-spin" /> : 'Finalizar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Results Dialog */}
        <Dialog open={showResultsDialog} onOpenChange={setShowResultsDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy size={24} className={finalResults?.passed ? 'text-green-500' : 'text-red-500'} />
                Resultados de la Evaluación
              </DialogTitle>
            </DialogHeader>
            
            {finalResults && (
              <div className="space-y-4 py-4">
                {/* Score */}
                <div className={`text-center p-6 rounded-lg ${finalResults.passed ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className="text-4xl font-bold mb-2" style={{ color: finalResults.passed ? '#16a34a' : '#dc2626' }}>
                    {finalResults.score.percentage}%
                  </p>
                  <p className="text-sm text-slate-600">
                    {finalResults.score.obtained} de {finalResults.score.max} puntos
                  </p>
                  <Badge className="mt-3" variant={finalResults.passed ? 'default' : 'destructive'}>
                    {finalResults.passed ? (
                      <><CheckCircle size={14} className="mr-1" /> APROBADO</>
                    ) : (
                      <><XCircle size={14} className="mr-1" /> NO APROBADO</>
                    )}
                  </Badge>
                </div>
                
                {/* Critical Failures */}
                {finalResults.criticalFailures.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                      <Warning size={18} />
                      Criterios críticos no cumplidos
                    </h4>
                    <ul className="text-sm text-red-600 space-y-1">
                      {finalResults.criticalFailures.map((failure, idx) => (
                        <li key={idx}>
                          • <strong>{failure.station}:</strong> {failure.criterio}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Station Summary */}
                <div className="space-y-2">
                  <h4 className="font-medium text-slate-700">Resumen por estación</h4>
                  {exam.estaciones.map(station => {
                    const score = calculateStationScore(station.id);
                    const percentage = score.max > 0 ? Math.round((score.obtained / score.max) * 100) : 0;
                    return (
                      <div key={station.id} className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">{station.nombre}</span>
                        <span className={`font-medium ${percentage >= 60 ? 'text-green-600' : 'text-red-600'}`}>
                          {score.obtained}/{score.max} ({percentage}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button onClick={() => navigate('/app/evaluations')} className="w-full gap-2">
                Volver a Evaluaciones
                <ArrowRight size={16} />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
